import { If } from '@shared/ui/if.component';
import React, { useState } from 'react';
import Spinner from 'react-bootstrap/Spinner';
import Container from 'typedi';
import { AnonymousShell } from '../../anonymous-shell/anonymous-shell.component';
import { useCurrentUser } from '../../global-hooks/use-current-user.hook';
import { Urls } from '../../urls';
import { LogInActionService } from './log-in-action.service';
import { LogInData } from './log-in.data';
import { LogInForm } from './log-in-form.component';
import { useHideDefaultLogin } from './use-hide-default-login.hook';
import { useOauth2Clients } from './use-oauth2-clients.hook';

export function LogIn(): React.ReactElement {

    const { loading: loadingUser, user } = useCurrentUser();
    const { loading: loadingOAuthClients, data } = useOauth2Clients();
    const { loading: loadingHideDefaultLogin, hideDefaultLogin } = useHideDefaultLogin();
    const [mfaError, setMfaError] = useState<string | undefined>();
    const [mfaAttemptsRemaining, setMfaAttemptsRemaining] = useState<number>(3);

    const isLoading = loadingUser || loadingOAuthClients || loadingHideDefaultLogin;
    if (user != null) {
        loggedIn();
        return <></>;
    }

    function handleMfaLogin(username: string, mfaToken: string, mfaCode: string, rememberMe: boolean): void {
        const loginData = Container.get(LogInData);
        loginData.mfaVerify(username, mfaToken, mfaCode, rememberMe).subscribe({
            next: (response) => {
                if (response.LoggedIn && response.User) {
                    loggedIn();
                } else if (response.Error) {
                    const attemptsLeft = mfaAttemptsRemaining - 1;
                    setMfaAttemptsRemaining(attemptsLeft);
                    if (attemptsLeft > 0) {
                        setMfaError(`Invalid verification code. ${attemptsLeft} attempt${attemptsLeft !== 1 ? 's' : ''} remaining.`);
                    } else {
                        setMfaError('Maximum attempts exceeded. Please start over.');
                    }
                } else {
                    setMfaError('Unknown error occurred during verification.');
                }
            },
            error: (err) => {
                console.error('MFA verification error:', err);
                setMfaError('Error verifying code. Please try again.');
            }
        });
    }

    function resetMfa(): void {
        setMfaError(undefined);
        setMfaAttemptsRemaining(3);
    }

    function handleResendMfaCode(username: string, mfaToken: string, onTokenUpdate: (newToken: string) => void): void {
        const loginData = Container.get(LogInData);
        loginData.resendMfaCode(username, mfaToken).subscribe({
            next: (response) => {
                if (response.Success && response.MfaToken) {
                    setMfaError(undefined);
                    onTokenUpdate(response.MfaToken);
                } else {
                    setMfaError('Failed to resend code. Please try again.');
                }
            },
            error: (err) => {
                console.error('Resend MFA code error:', err);
                setMfaError('Error resending code. Please try again.');
            }
        });
    }

    return (
        <AnonymousShell
            title='Login'
            render={() =>
                <div>
                    <If
                        if={isLoading}
                        then={() => (
                            <Spinner animation='border' variant='primary' />
                        )} />
                    <If
                        if={!isLoading && user == null}
                        then={() => (
                            <LogInForm
                                loggedIn={() => loggedIn()}
                                mfaLogin={handleMfaLogin}
                                mfaError={mfaError}
                                mfaAttemptsRemaining={mfaAttemptsRemaining}
                                resetMfa={resetMfa}
                                resendMfaCode={handleResendMfaCode}
                                oauth2={data}
                                hideDefaultLogin={hideDefaultLogin} />
                        )} />
                </div>
            } />
    );
}

function isUrl(urlString: string) {
    try {
        // const r = new RegExp('^(?:[a-z+]+:)?//', 'i');
        // return r.test(urlString);

        return new URL(urlString);
        // return true;
    } catch (_) {
        return undefined;
    }
}

function validateHostName(toString: string) {
    // not url, return original value
    const toUrl = isUrl(toString);
    if (!toUrl) { return toString; }

    // make sure url is on correct domain
    // host includes port number, hostname does not

    // should we treat www.example.com and example.com as equal?
    const windowHostname = window.location.hostname.toLocaleLowerCase().replace('www.', '');
    const urlHostname = toUrl.hostname.toLocaleLowerCase().replace('www.', '');

    if (windowHostname === urlHostname) {
        return toString;
    }

    // to is a url, but not on the same domain
    return '';
}

function loggedIn(): void {
    const navToUrl = validateHostName(getNavigateTo());
    if (navToUrl.length > 0) {
        window.location.href = navToUrl;
    } else {
        const value = Container.get(LogInActionService).getLogInAction();
        if (value === 'selectPlugin') {
            window.location.href = Urls.makeAppSelectionUrl().toString();
        } else {
            // load last app is the default action.
            window.location.href = Urls.makeSwitchAppUrl({ lastApp: true }).toString();
        }
    }
}

function getNavigateTo(): string {
    const url = new URL(window.location.href);
    return url.searchParams.get('to') ?? '';
}
